import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { refreshCompanySettings } from '../hooks/useCompanySettings';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============ AXIOS INTERCEPTOR: refresh automático de token ============
// Garante que enquanto o computador estiver ligado o operador não desloga.
// Em qualquer 401, tenta /auth/refresh uma vez e refaz a requisição.
let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error) => {
  refreshQueue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve()));
  refreshQueue = [];
};

axios.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error.config;
    if (!original) return Promise.reject(error);
    const url = original.url || '';
    // Não tentar refresh em endpoints de auth para evitar loop
    if (
      error.response?.status === 401 &&
      !original._retry &&
      !url.includes('/auth/login') &&
      !url.includes('/auth/refresh') &&
      !url.includes('/auth/logout')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({
            resolve: () => resolve(axios({ ...original, _retry: true })),
            reject,
          });
        });
      }
      original._retry = true;
      isRefreshing = true;
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        processQueue(null);
        return axios({ ...original, _retry: true });
      } catch (refreshErr) {
        processQueue(refreshErr);
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo deu errado. Por favor, tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  // KEEP-ALIVE: enquanto o user estiver logado, faz refresh proativo a cada 5 minutos
  // garantindo que a sessão nunca expire enquanto o computador estiver ligado e o app aberto.
  useEffect(() => {
    if (!user) return;
    const id = setInterval(() => {
      axios.post(`${API}/auth/refresh`, {}, { withCredentials: true }).catch(() => {});
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [user]);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      refreshCompanySettings(); // força recarga (limpa fallback de chamadas pré-login)
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      refreshCompanySettings(); // recarrega settings após login (limpa cache de sessão anterior)
      return { success: true, user: data };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const register = async (email, password, name, role = 'caixa') => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, { email, password, name, role }, { withCredentials: true });
      setUser(data);
      return { success: true };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};